import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'contexts/globalContext';

type CopyOptions = {
	successMessage?: string;
	errorMessage?: string;
	skipSuccessToast?: boolean;
};

const fallbackCopy = (text: string) => {
	const textarea = document.createElement('textarea');
	textarea.value = text;
	textarea.setAttribute('readonly', '');
	textarea.style.position = 'absolute';
	textarea.style.left = '-9999px';
	document.body.appendChild(textarea);
	textarea.select();
	const result = document.execCommand('copy');
	document.body.removeChild(textarea);
	return result;
};

export const useCopy = () => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();

	const copy = useCallback(
		async (value: string, options?: CopyOptions) => {
			if (!value) return false;

			try {
				if (navigator?.clipboard?.writeText) {
					await navigator.clipboard.writeText(value);
				} else {
					const copied = fallbackCopy(value);
					if (!copied) {
						throw new Error('Fallback copy failed');
					}
				}

				if (!options?.skipSuccessToast) {
					toastRef?.current?.show({
						severity: 'success',
						life: 2500,
						summary: t('misc.success'),
						detail: options?.successMessage || t('misc.copiedToClipboard'),
					});
				}

				return true;
			} catch (error) {
				toastRef?.current?.show({
					severity: 'error',
					life: 3000,
					summary: t('misc.error'),
					detail: options?.errorMessage || t('misc.copyToClipboardError'),
				});
				return false;
			}
		},
		[t, toastRef]
	);

	return { copy };
};
